import { A11yModule } from "@angular/cdk/a11y";
import { CommonModule, DOCUMENT } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, EventEmitter, HostListener, Input, Output, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { catchError, of } from "rxjs";
import { AppointmentDisplay, HearingDetailDisplayError, formatStatus, mapAppointmentDisplay, mapHearingDetailError } from "./hearing-detail.models";
import { HearingDetailService, ManagedHearingSchedulingBasis } from "./hearing-detail.service";
import { AssignableOfficial, HearingOfficialAssignment, HearingOfficialAssignmentService } from "./hearing-official-assignment.service";
import { CaseContextRecordsService } from "@app/case-context/services/case-context-records.service";
import { formatPersonNameLastFirst } from "@app/shared/util/person-name";
import { InmateOffenseRecord } from "@app/case-context/models/inmate-offense-record.model";
import { PhmsReturnContext, buildReturnExtras, hearingCycleCommands } from "../navigation/phms-detail-navigation";
import {
  EligibilityDetermination,
  HEARING_ELIGIBILITY_DETERMINATION_TYPE_CODE,
  toEligibilityStatusSummary,
} from "@app/phms/eligibility/eligibility-determination.model";
import { EligibilityDeterminationService } from "@app/phms/eligibility/eligibility-determination.service";
import { PhmsDocument } from "@app/phms/documents/phms-document.model";
import { PhmsDocumentService } from "@app/phms/documents/phms-document.service";
import {
  PacketDocumentStatusConfig,
  dedupeDocumentsByArtifact,
  packetDocumentStatus,
  sortCaseDocuments,
} from "@app/phms/documents/phms-create-packet.model";
import { PHMS_SUBJECT_TERMINOLOGY } from "@app/phms/phms-subject.terminology";
import { PHMS_HEARING_TERMINOLOGY } from "@app/phms/phms-hearing.terminology";
import { PHMS_DOCUMENT_TERMINOLOGY } from "@app/phms/phms-document.terminology";
import { PHMS_OPERATIONAL_TERMINOLOGY } from "@app/phms/phms-operational.terminology";
import { PHMS_ELIGIBILITY_TERMINOLOGY } from "@app/phms/phms-eligibility.terminology";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

type ChargesState = "loading" | "ready" | "empty";
type DocumentsState = "loading" | "ready" | "empty" | "error";

/**
 * Hearing-detail flyout (#PHMS-SCH-7). Opened from the scheduling calendar, it is intentionally a thin
 * calendar surface: the appointment basics (date/time/location/format/status, same source of truth as
 * the grid), the inmate's charges, and the coordinator/admin-gated calendar actions Move Date (inline
 * reschedule in place) and Unschedule (a visible button that, once confirmed, returns the hearing to
 * the Awaiting-scheduling backlog, #1526). Gated via {@link canReschedule}/canMutateHearings; server
 * stays authoritative (#PHMS-SCH-6).
 */
@Component({
  selector: "phms-hearing-detail-flyout",
  standalone: true,
  imports: [CommonModule, A11yModule],
  templateUrl: "./hearing-detail-flyout.component.html",
  styleUrl: "./hearing-detail-flyout.component.scss",
})
export class HearingDetailFlyoutComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly detailService = inject(HearingDetailService);
  private readonly recordsService = inject(CaseContextRecordsService);
  private readonly router = inject(Router);
  private readonly eligibilityService = inject(EligibilityDeterminationService);
  private readonly officialAssignmentService = inject(HearingOfficialAssignmentService);
  private readonly documentService = inject(PhmsDocumentService);
  readonly subjectText = createTerminologyUsageSignals(PHMS_SUBJECT_TERMINOLOGY.hearingDetail);
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.hearingDetailFlyout);
  readonly documentText = createTerminologyUsageSignals(PHMS_DOCUMENT_TERMINOLOGY.hearingDetailFlyout);
  readonly operationalText = createTerminologyUsageSignals(PHMS_OPERATIONAL_TERMINOLOGY.hearingDetailFlyout);
  readonly eligibilityText = createTerminologyUsageSignals(PHMS_ELIGIBILITY_TERMINOLOGY.hearingDetailFlyout);

  constructor() {
    // The rail is rendered beneath the application shell's content stacking context. Lift that
    // context while this modal exists so the scrim and full-screen mobile rail also cover the
    // persistent side navigation.
    this.document.body.classList.add("hearing-detail-rail-open");
    this.destroyRef.onDestroy(() => this.document.body.classList.remove("hearing-detail-rail-open"));
  }

  readonly isLoading = signal(false);
  readonly errorState = signal<HearingDetailDisplayError | null>(null);
  /** Calendar-first appointment header (the primary content), from the calendar appointment endpoint. */
  readonly appointment = signal<AppointmentDisplay | null>(null);
  readonly activeProceedingId = signal<number | null>(null);

  /** Incarcerated person behind the active proceeding, resolved from the appointment payload. */
  readonly incarceratedPersonId = signal<number | null>(null);

  /** The inmate's charges (offense records). */
  readonly charges = signal<InmateOffenseRecord[]>([]);
  readonly chargesState = signal<ChargesState>("loading");
  readonly managedSchedulingBasis = signal<ManagedHearingSchedulingBasis | null>(null);
  private currentSchedulingRequestId: number | null = null;
  @Input() set schedulingRequestId(value: number | null) {
    if (value === this.currentSchedulingRequestId) return;
    this.currentSchedulingRequestId = value;
    if (this.appointment()) this.loadEligibility(this.incarceratedPersonId());
  }
  get schedulingRequestId(): number | null { return this.currentSchedulingRequestId; }
  readonly hearingEligibility = signal<EligibilityDetermination | null>(null);
  readonly isLoadingEligibility = signal(false);
  readonly isCalculatingEligibility = signal(false);
  readonly eligibilityActionMessage = signal<string | null>(null);
  readonly documents = signal<PhmsDocument[]>([]);
  readonly documentsState = signal<DocumentsState>("loading");

  /** The parole official assigned to this hearing (story 1444); null when unassigned. */
  readonly assignedOfficial = signal<HearingOfficialAssignment | null>(null);
  readonly isLoadingOfficial = signal(false);

  /** Assignable officials for the assign picker; loaded lazily when the picker opens. */
  readonly assignableOfficials = signal<AssignableOfficial[]>([]);

  /** Whether the inline assign-official picker is expanded (assign-gated roles only). */
  readonly showAssignOfficial = signal(false);

  /** True while an assign/remove official call is in flight. */
  readonly isSavingOfficial = signal(false);

  /** Best-effort failure message for the assign-official controls. */
  readonly officialActionMessage = signal<string | null>(null);

  /** "Last, First" presentation of the assigned official, or null when unassigned. */
  readonly assignedOfficialLabel = computed(() => {
    const assignment = this.assignedOfficial();
    return assignment ? formatPersonNameLastFirst(assignment.officialDisplayName) : null;
  });

  /** Whether the inline Move-date reschedule form is expanded (coordinator/admin only). */
  readonly showMoveDate = signal(false);

  /** Whether the Unschedule confirmation prompt is shown (gates the destructive unschedule, #1526). */
  readonly showCancelConfirm = signal(false);

  /** Inline reschedule state: the chosen new start (native datetime-local string or null). */
  moveDateFormValues: { scheduledStart: string | Date | null } = {
    scheduledStart: null,
  };

  /** Current value formatted for the native datetime-local input (local "YYYY-MM-DDTHH:mm"). */
  get moveDateInputValue(): string {
    const raw = this.moveDateFormValues.scheduledStart;
    if (!raw) {
      return "";
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  /** datetime-local emits a local "YYYY-MM-DDTHH:mm" string; store it as the pending new start. */
  onMoveDateInput(value: string): void {
    this.moveDateFormValues = { scheduledStart: value || null };
  }

  readonly hasCharges = computed(() => this.charges().length > 0);
  readonly hearingEligibilitySummary = computed(() => toEligibilityStatusSummary(this.hearingEligibility()));
  readonly appointmentDate = computed(() => appointmentDateParts(this.appointment()?.scheduledStartUtc ?? null));
  readonly controllingCharge = computed(() => {
    const charges = this.charges();
    return charges.find((record) => /controlling/i.test(record.summary ?? "")) ?? charges[0] ?? null;
  });
  readonly documentRows = computed(() =>
    sortCaseDocuments(dedupeDocumentsByArtifact(this.documents()), "date-added"));
  readonly visibleDocuments = computed(() => this.documentRows().slice(0, 3));
  readonly documentCount = computed(() => this.documentRows().length);
  readonly hasAttentionItems = computed(() =>
    this.isLoadingOfficial()
    || this.assignedOfficial() === null
    || this.isLoadingEligibility()
    || (this.schedulingRequestId ? this.managedSchedulingBasis() === null || !!this.managedSchedulingBasis()?.basisIssue : this.hearingEligibility() === null));
  readonly eligibilityStatusLabel = computed(() => {
    if (this.isCalculatingEligibility()) {
      return "Calculating";
    }
    return eligibilityStatusLabel(this.hearingEligibilitySummary()?.statusCode);
  });
  readonly eligibilityTone = computed(() => eligibilityTone(this.hearingEligibilitySummary()?.statusCode));
  readonly documentStatus = (document: PhmsDocument): PacketDocumentStatusConfig => packetDocumentStatus(document);

  /**
   * Whether the coordinator/admin-gated calendar actions (Move Date, Cancel) are visible
   * (#PHMS-SCH-5/#PHMS-SCH-6). The parent passes its single `canMutateHearings` signal through so the
   * flyout and calendar share one gate. Server authorization remains authoritative; this is UX-only.
   */
  @Input() canReschedule = false;

  /**
   * Whether the assign/change-official controls are visible (story 1444). The parent passes its
   * workflow_runtime.assign-derived gate through; server authorization stays authoritative.
   */
  @Input() canAssignOfficial = false;

  /** Return context preserved for the originating surface (story 981); retained for the parent binding. */
  @Input() returnContext: PhmsReturnContext | null = null;

  @Input({ required: true })
  set scheduledProceedingId(value: number | null | undefined) {
    const id = toPositiveInt(value);
    if (id === this.activeProceedingId()) {
      return;
    }
    this.activeProceedingId.set(id);
    if (id === null) {
      this.reset();
      return;
    }
    this.load(id);
  }
  get scheduledProceedingId(): number | null {
    return this.activeProceedingId();
  }

  /** Emitted when the user dismisses the flyout (overlay/close button). */
  @Output() readonly closed = new EventEmitter<void>();

  /**
   * Emitted when a coordinator/admin moves the appointment to a new window (#PHMS-SCH-6). The parent
   * reschedules the proceeding in place via the schedule write endpoint and reloads the calendar.
   */
  @Output() readonly moveDate = new EventEmitter<MoveDateRequest>();

  /** Emitted when a coordinator/admin cancels (removes) the proceeding from the calendar (#PHMS-SCH-6). */
  @Output() readonly removeFromCalendar = new EventEmitter<number>();

  private load(scheduledProceedingId: number): void {
    this.isLoading.set(true);
    this.errorState.set(null);
    this.appointment.set(null);
    this.incarceratedPersonId.set(null);
    this.charges.set([]);
    this.chargesState.set("loading");
    this.hearingEligibility.set(null);
    this.isLoadingEligibility.set(false);
    this.isCalculatingEligibility.set(false);
    this.eligibilityActionMessage.set(null);
    this.documents.set([]);
    this.documentsState.set("loading");
    this.showMoveDate.set(false);
    this.showCancelConfirm.set(false);
    this.moveDateFormValues = { scheduledStart: null };
    this.assignedOfficial.set(null);
    this.isLoadingOfficial.set(true);
    this.showAssignOfficial.set(false);
    this.isSavingOfficial.set(false);
    this.officialActionMessage.set(null);
    this.loadAssignedOfficial(scheduledProceedingId);

    // Calendar appointment is the primary source: it drives the header, the incarcerated-person
    // resolution (for charges) and the error state.
    this.detailService
      .getAppointment(scheduledProceedingId)
      .pipe(
        catchError((error: unknown) => {
          this.errorState.set(mapHearingDetailError(error));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((appointment) => {
        this.isLoading.set(false);
        if (!appointment) {
          return;
        }
        const display = mapAppointmentDisplay(appointment);
        this.appointment.set(display);
        this.incarceratedPersonId.set(display.incarceratedPersonId);
        this.loadCharges(display.incarceratedPersonId);
        this.loadEligibility(display.incarceratedPersonId);
        this.loadDocuments(display.incarceratedPersonId);
      });
  }

  /**
   * Assigned official is a best-effort section (story 1444): a 404 (no assignment) or any other
   * failure renders the "None" state rather than an error.
   */
  private loadAssignedOfficial(scheduledProceedingId: number): void {
    this.isLoadingOfficial.set(true);
    this.officialAssignmentService
      .getAssignment(scheduledProceedingId)
      .pipe(
        catchError(() => of<HearingOfficialAssignment | null>(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((assignment) => {
        this.assignedOfficial.set(assignment);
        this.isLoadingOfficial.set(false);
      });
  }

  /** Charges are a best-effort section keyed on the inmate; a failure renders the empty state. */
  private loadCharges(incarceratedPersonId: number | null): void {
    if (incarceratedPersonId === null) {
      this.charges.set([]);
      this.chargesState.set("empty");
      return;
    }
    this.chargesState.set("loading");
    this.recordsService
      .getByIncarceratedPerson(incarceratedPersonId)
      .pipe(
        catchError(() => of<InmateOffenseRecord[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((records) => {
        this.charges.set(records ?? []);
        this.chargesState.set((records ?? []).length > 0 ? "ready" : "empty");
      });
  }

  private loadEligibility(incarceratedPersonId: number | null): void {
    this.hearingEligibility.set(null);
    this.eligibilityActionMessage.set(null);
    this.managedSchedulingBasis.set(null);
    if (this.schedulingRequestId) {
      const requestId = this.schedulingRequestId;
      const proceedingId = this.activeProceedingId();
      this.isLoadingEligibility.set(true);
      this.detailService.getSchedulingBasis(requestId).pipe(
        catchError(() => { this.eligibilityActionMessage.set('The governed hearing scheduling basis is unavailable. Use the hearing workflow to review its current evidence.'); return of(null); }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(result => {
        if (this.schedulingRequestId !== requestId || this.activeProceedingId() !== proceedingId) return;
        this.managedSchedulingBasis.set(result?.requestId === requestId && result.scheduledProceedingId === proceedingId ? result : null);
        this.isLoadingEligibility.set(false);
      });
      return;
    }
    if (incarceratedPersonId === null) {
      this.isLoadingEligibility.set(false);
      return;
    }

    this.isLoadingEligibility.set(true);
    this.eligibilityService
      .getCurrent(incarceratedPersonId, HEARING_ELIGIBILITY_DETERMINATION_TYPE_CODE)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.hearingEligibility.set(result);
        this.isLoadingEligibility.set(false);
      });
  }

  /** Compact, read-only document preview for the rail. Full management stays in Case Context. */
  private loadDocuments(incarceratedPersonId: number | null): void {
    this.documents.set([]);
    if (incarceratedPersonId === null) {
      this.documentsState.set("empty");
      return;
    }

    this.documentsState.set("loading");
    this.documentService
      .getForIncarceratedPerson(incarceratedPersonId)
      .pipe(
        catchError(() => {
          this.documentsState.set("error");
          return of<PhmsDocument[]>([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((documents) => {
        if (this.documentsState() === "error") {
          return;
        }
        this.documents.set(documents ?? []);
        this.documentsState.set((documents ?? []).length > 0 ? "ready" : "empty");
      });
  }

  calculateEligibility(): void {
    if (this.schedulingRequestId) { this.eligibilityActionMessage.set("Use the hearing workflow to reassess its governed eligibility evidence."); return; }
    const incarceratedPersonId = this.incarceratedPersonId();
    if (incarceratedPersonId === null || this.isCalculatingEligibility()) {
      return;
    }

    this.isCalculatingEligibility.set(true);
    this.eligibilityActionMessage.set(null);

    this.eligibilityService
      .calculateCurrentHearingEligibility(incarceratedPersonId)
      .pipe(
        catchError((error: unknown) => {
          this.eligibilityActionMessage.set(
            eligibilityCalculationErrorMessage(
              error,
              this.eligibilityText.calculationPermissionDenied(),
              this.eligibilityText.calculationSignInAgain(),
              this.eligibilityText.calculationFailed(),
            ),
          );
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result) {
          this.hearingEligibility.set(result);
        }
        this.isCalculatingEligibility.set(false);
      });
  }

  private reset(): void {
    this.isLoading.set(false);
    this.errorState.set(null);
    this.appointment.set(null);
    this.incarceratedPersonId.set(null);
    this.charges.set([]);
    this.chargesState.set("loading");
    this.hearingEligibility.set(null);
    this.isLoadingEligibility.set(false);
    this.isCalculatingEligibility.set(false);
    this.eligibilityActionMessage.set(null);
    this.documents.set([]);
    this.documentsState.set("loading");
    this.showMoveDate.set(false);
    this.showCancelConfirm.set(false);
    this.moveDateFormValues = { scheduledStart: null };
    this.assignedOfficial.set(null);
    this.isLoadingOfficial.set(false);
    this.showAssignOfficial.set(false);
    this.isSavingOfficial.set(false);
    this.officialActionMessage.set(null);
  }

  /**
   * Assign/Change official -> toggle the inline picker (assign-gated roles only). The assignable
   * officials list loads lazily on first open.
   */
  toggleAssignOfficial(): void {
    if (!this.canAssignOfficial) {
      return;
    }
    this.officialActionMessage.set(null);
    this.showAssignOfficial.update((shown) => {
      const next = !shown;
      if (next && this.assignableOfficials().length === 0) {
        this.loadAssignableOfficials();
      }
      return next;
    });
  }

  /** Confirm a picker selection -> assign (or replace) the hearing's official (story 1444). */
  assignOfficial(rawUserId: string): void {
    const id = this.activeProceedingId();
    const officialUserId = Number.parseInt(rawUserId ?? "", 10);
    if (id === null || !this.canAssignOfficial || this.isSavingOfficial() || !Number.isInteger(officialUserId) || officialUserId <= 0) {
      return;
    }

    this.isSavingOfficial.set(true);
    this.officialActionMessage.set(null);
    this.officialAssignmentService
      .assign(id, officialUserId)
      .pipe(
        catchError(() => {
          this.officialActionMessage.set("The official could not be assigned.");
          return of<HearingOfficialAssignment | null>(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((assignment) => {
        this.isSavingOfficial.set(false);
        if (assignment) {
          this.assignedOfficial.set(assignment);
          this.showAssignOfficial.set(false);
        }
      });
  }

  /** Remove the hearing's official assignment (assign-gated roles only). */
  removeOfficial(): void {
    const id = this.activeProceedingId();
    if (id === null || !this.canAssignOfficial || this.isSavingOfficial() || this.assignedOfficial() === null) {
      return;
    }

    this.isSavingOfficial.set(true);
    this.officialActionMessage.set(null);
    this.officialAssignmentService
      .unassign(id)
      .pipe(
        catchError(() => {
          this.officialActionMessage.set("The official could not be removed.");
          return of<{ failed: true } | void>({ failed: true });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((outcome) => {
        this.isSavingOfficial.set(false);
        if (!(outcome && (outcome as { failed?: boolean }).failed)) {
          this.assignedOfficial.set(null);
          this.showAssignOfficial.set(false);
        }
      });
  }

  /** "Last, First" presentation for an assign-picker option. */
  officialOptionLabel(official: AssignableOfficial): string {
    return formatPersonNameLastFirst(official.displayName);
  }

  trackByOfficial(_index: number, official: AssignableOfficial): number {
    return official.userId;
  }

  private loadAssignableOfficials(): void {
    this.officialAssignmentService
      .getOfficials()
      .pipe(
        catchError(() => {
          this.officialActionMessage.set("Assignable officials could not be loaded.");
          return of<AssignableOfficial[]>([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((officials) => {
        this.assignableOfficials.set(officials ?? []);
      });
  }

  close(): void {
    this.closed.emit();
  }

  /**
   * Open the proceeding-scoped hearing-cycle overview. The workspace derives the correct next action
   * from milestones and open requirements instead of assuming every calendar click needs At-Hearing.
   */
  openHearingWorkspace(): void {
    const id = this.activeProceedingId();
    if (id === null) {
      return;
    }
    this.closed.emit();
    void this.router.navigate(hearingCycleCommands(id, "overview"), buildReturnExtras(this.returnContext));
  }

  /** Opens the existing full Case Context Documents tab and preserves calendar return context. */
  openAllDocuments(): void {
    const incarceratedPersonId = this.incarceratedPersonId();
    if (incarceratedPersonId === null) {
      return;
    }
    this.closed.emit();
    void this.router.navigate(
      ["/case-context/inmate", incarceratedPersonId],
      { ...buildReturnExtras(this.returnContext), fragment: "documents" },
    );
  }

  @HostListener("document:keydown.escape")
  closeFromKeyboard(): void {
    if (this.showCancelConfirm()) {
      this.dismissUnschedule();
      return;
    }
    if (this.showMoveDate()) {
      this.showMoveDate.set(false);
      return;
    }
    this.close();
  }

  /**
   * Move Date -> toggle the inline reschedule form (coordinator/admin only). Seeds the form with the
   * current scheduled start so the picker opens on the existing window.
   */
  toggleMoveDate(): void {
    if (!this.canReschedule) {
      return;
    }
    this.showMoveDate.update((shown) => {
      const next = !shown;
      if (next) {
        this.moveDateFormValues = {
          scheduledStart: this.appointment()?.scheduledStartUtc ?? null,
        };
      }
      return next;
    });
  }

  /**
   * Confirm Move Date -> emit the new window to the parent for an in-place reschedule. The new end
   * preserves the appointment's original duration (defaulting to two hours).
   */
  confirmMoveDate(): void {
    const id = this.activeProceedingId();
    const raw = this.moveDateFormValues.scheduledStart;
    if (id === null || !this.canReschedule || !raw) {
      return;
    }
    const start = new Date(raw);
    if (Number.isNaN(start.getTime())) {
      return;
    }
    const end = new Date(start.getTime() + durationMs(this.appointment()));
    this.moveDate.emit({
      scheduledProceedingId: id,
      scheduledStartUtc: start.toISOString(),
      scheduledEndUtc: end.toISOString(),
    });
    this.showMoveDate.set(false);
  }

  /** Unschedule -> open the confirmation prompt (nothing changes until confirmed, #1526). */
  requestUnschedule(): void {
    if (this.activeProceedingId() === null || !this.canReschedule) {
      return;
    }
    this.showMoveDate.set(false);
    this.showCancelConfirm.set(true);
  }

  /** Confirm the unschedule -> bubble to the parent calendar's SCH-6 unschedule action. */
  confirmUnschedule(): void {
    const id = this.activeProceedingId();
    if (id === null || !this.canReschedule) {
      return;
    }
    this.showCancelConfirm.set(false);
    this.removeFromCalendar.emit(id);
  }

  /** Dismiss the unschedule prompt and keep the hearing on the calendar. */
  dismissUnschedule(): void {
    this.showCancelConfirm.set(false);
  }

  chargeHeadline(record: InmateOffenseRecord): string {
    const summary = (record.summary ?? "").trim();
    if (summary) {
      return summary;
    }
    return (record.recordCategoryCode ?? "").trim() || "Offense";
  }

  chargeMeta(record: InmateOffenseRecord): string {
    return [
      record.statuteReference?.trim(),
      record.caseNumber?.trim() ? `Case ${record.caseNumber!.trim()}` : null,
      record.dispositionCode?.trim(),
      record.severityCode?.trim(),
    ]
      .filter((part): part is string => Boolean(part))
      .join(" · ");
  }

  trackByCharge(_index: number, record: InmateOffenseRecord): number {
    return record.id;
  }

  chargeSeverityLabel(record: InmateOffenseRecord | null): string | null {
    const severity = record?.severityCode?.trim();
    return severity ? formatStatus(severity) : null;
  }

  documentIconKind(document: PhmsDocument): "people" | "legal" | "file" {
    if (["psychological-evaluation", "board-report", "investigation-report"].includes(document.documentCategoryCode)) {
      return "people";
    }
    if (document.documentCategoryCode === "legal-filing") {
      return "legal";
    }
    return "file";
  }

  trackByDocument(_index: number, document: PhmsDocument): string {
    return document.artifactPublicId;
  }
}

function eligibilityCalculationErrorMessage(
  error: unknown,
  permissionDenied: string,
  signInAgain: string,
  calculationFailed: string,
): string {
  if (error instanceof HttpErrorResponse && error.status === 403) {
    return permissionDenied;
  }

  if (error instanceof HttpErrorResponse && error.status === 401) {
    return signInAgain;
  }

  return calculationFailed;
}

/** New appointment window requested from the flyout's inline Move-date reschedule form. */
export interface MoveDateRequest {
  scheduledProceedingId: number;
  scheduledStartUtc: string;
  scheduledEndUtc: string;
}

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

/** Original appointment duration in ms, defaulting to two hours when the window is absent/invalid. */
function durationMs(appointment: AppointmentDisplay | null): number {
  const startIso = appointment?.scheduledStartUtc;
  const endIso = appointment?.scheduledEndUtc;
  if (startIso && endIso) {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
      return end - start;
    }
  }
  return TWO_HOURS_MS;
}

function toPositiveInt(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

interface AppointmentDateParts {
  weekday: string;
  day: string;
  monthYear: string;
  compactLabel: string;
}

function appointmentDateParts(value: string | null): AppointmentDateParts | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return {
    weekday: new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(date).toUpperCase(),
    day: new Intl.DateTimeFormat(undefined, { day: "numeric" }).format(date),
    monthYear: new Intl.DateTimeFormat(undefined, { month: "short", year: "numeric" }).format(date).toUpperCase(),
    compactLabel: new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date),
  };
}

function eligibilityStatusLabel(statusCode: string | null | undefined): string {
  switch ((statusCode ?? "").trim().toLowerCase()) {
    case "eligible": return "Eligible";
    case "not_eligible": return "Not eligible";
    case "pending_information": return "Pending information";
    case "permanently_ineligible": return "Permanently ineligible";
    case "manually_overridden": return "Manual override";
    default: return "Not calculated";
  }
}

function eligibilityTone(statusCode: string | null | undefined): "success" | "warning" | "danger" | "info" | "neutral" {
  switch ((statusCode ?? "").trim().toLowerCase()) {
    case "eligible": return "success";
    case "not_eligible":
    case "pending_information": return "warning";
    case "permanently_ineligible": return "danger";
    case "manually_overridden": return "info";
    default: return "neutral";
  }
}
